/**
 * Copying text to the clipboard, with a fallback that works where the async
 * Clipboard API does not.
 *
 * `navigator.clipboard` is absent in non-secure contexts and can be
 * permission-gated or throw inside in-app browsers (WebViews) — which is
 * exactly where a buyer needs to copy a checkout link. The selection-based
 * fallback is deprecated but remains the only route that works there.
 */

const copyViaSelection = (text: string): boolean => {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    // Keep the field out of sight without making it unselectable: iOS refuses
    // to copy from an element that is display:none or visibility:hidden.
    field.style.position = "fixed";
    field.style.top = "0";
    field.style.left = "0";
    field.style.width = "1px";
    field.style.height = "1px";
    field.style.padding = "0";
    field.style.border = "none";
    field.style.opacity = "0";

    document.body.appendChild(field);
    try {
        field.select();
        field.setSelectionRange(0, text.length);
        // Deprecated, but the only copy route left in the non-secure contexts
        // and WebViews this fallback exists for.
        return document.execCommand("copy"); // NOSONAR
    } catch {
        return false;
    } finally {
        field.remove();
    }
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // Fall through — the selection fallback still has a chance.
    }
    return copyViaSelection(text);
};
